'use client';

import { useEffect, useState } from 'react';
import './StarRating.scss';

interface StarRatingProps {
  sketchId: string;
  inline?: boolean;
  showCount?: boolean;
}

export default function StarRating({
  sketchId,
  inline = true,
  showCount = true,
}: StarRatingProps) {
  const [avgStars, setAvgStars] = useState<number>(0);
  const [totalRatings, setTotalRatings] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRatingStats = async () => {
      try {
        const response = await fetch(`/api/sketches/${sketchId}/ratings`);
        const data = await response.json();
        
        if (data.success && data.stats) {
          setAvgStars(data.stats.avgStars);
          setTotalRatings(data.stats.totalRatings);
        }
      } catch (err) {
        console.error('Error fetching rating stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRatingStats();
  }, [sketchId]);

  if (loading || totalRatings === 0) {
    return null; // Don't show anything if no ratings yet
  }

  const renderStars = () => {
    const fullStars = Math.floor(avgStars);
    const hasHalfStar = avgStars % 1 >= 0.5;
    const stars = [];

    for (let i = 0; i < 3; i++) {
      if (i < fullStars) {
        stars.push(<span key={i} className="star filled">★</span>);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<span key={i} className="star half">★</span>);
      } else {
        stars.push(<span key={i} className="star empty">★</span>);
      }
    }

    return stars;
  };

  return (
    <div className={`star-rating ${inline ? 'inline' : 'block'}`}>
      <div className="stars">{renderStars()}</div>
      {showCount && (
        <span className="rating-count">
          {avgStars.toFixed(1)} ({totalRatings})
        </span>
      )}
    </div>
  );
}
